import { I18nPluralPipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPostalCode } from '@bindoc-projects/geographic-domain-lib';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { BdPostalCodeStore } from '../store/postal-code.store';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';

export type BdPostalCodeResolveStrategy =
  'FIRST' | 'SUMMARY' | 'DETAIL' | 'RAW';
export type BdPostalCodeResolveType<T extends BdPostalCodeResolveStrategy> =
  T extends 'RAW' ? Array<BdPostalCode> : BdPostalCode;

@Injectable({
  providedIn: 'root',
})
export class BdPostalCodeService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _pluralPipe = inject(I18nPluralPipe);
  private readonly _store: BdPostalCodeStore;
  private _cache: { [postalCode: string]: Observable<BdPostalCode[]> } = {};

  constructor() {
    const environmentService = inject(BdAppEnvironmentService);
    this._store = new BdPostalCodeStore(
      environmentService.load(ApiEndpointKey.BD_DISTRICT_URL),
    );
  }

  public findOne<T extends BdPostalCodeResolveStrategy>(
    postalCode: string,
    resolveStrategy: T,
  ): Observable<BdPostalCodeResolveType<T>> {
    const url = this._store.findOne(postalCode);

    if (this._cache[postalCode]) {
      return this._cache[postalCode].pipe(
        map((response) => this._resolve(response, resolveStrategy)),
      );
    }

    this._cache[postalCode] = this._httpClient
      .get<{ [postalCode: string]: PostalCodeResponse[] }>(url)
      .pipe(
        map((response) => response[postalCode]),
        map((response) => BdPostalCode.fromObjectArray(response)),
        shareReplay(1),
      );

    return this._cache[postalCode].pipe(
      map((response) => this._resolve(response, resolveStrategy)),
    );
  }

  public find(query: string = null): Observable<BdPostalCode[]> {
    const url = this._store.index();
    let params: HttpParams = new HttpParams();

    if (query) {
      params = params.append('q', query);
    }

    return this._httpClient
      .get<PostalCodeResponse[]>(url, { params: params })
      .pipe(map((it) => BdPostalCode.fromObjectArray(it)));
  }

  public findAll<T extends BdPostalCodeResolveStrategy>(
    postalCodes: string[],
    resolveStrategy: T,
    storeInCache: boolean = true,
  ): Observable<{ [postalCode: string]: BdPostalCodeResolveType<T> }> {
    if (!postalCodes || postalCodes.length === 0) {
      return of({});
    }

    const url = this._store.index();
    const body: { postalCodes: string[] } = { postalCodes: postalCodes };

    return this._httpClient
      .post<{ [postalCode: string]: PostalCodeResponse[] }>(url, body)
      .pipe(
        map((response) => {
          const result: { [postalCode: string]: BdPostalCodeResolveType<T> } =
            {};
          Object.keys(response).map((postalCode) => {
            const postalCodesForCurrent = BdPostalCode.fromObjectArray(
              response[postalCode],
            );
            if (storeInCache) {
              this._cache[postalCode] = of(postalCodesForCurrent);
            }

            result[postalCode] = this._resolve(
              postalCodesForCurrent,
              resolveStrategy,
            );
          });

          return result;
        }),
      );
  }

  public findCenterPoint(
    postalCodes: string[],
  ): Observable<Map<string, { lat: number; lon: number }>> {
    const url = this._store.postalCodes();
    return this._httpClient
      .post<{
        [postalCode: string]: { lat: number; lon: number };
      }>(url, { postalCodes: postalCodes })
      .pipe(
        map((response) => {
          const result: Map<string, { lat: number; lon: number }> = new Map<
            string,
            {
              lat: number;
              lon: number;
            }
          >();
          Object.entries(response).forEach((it) => result.set(it[0], it[1]));
          return result;
        }),
      );
  }

  public administrativeDistrictMapping(): Observable<{
    [postalCode: string]: number;
  }> {
    const url = `${this._store.index()}administrative-districts/mappings/`;

    return this._httpClient.get<{ [postalCode: string]: number }>(url);
  }

  protected _resolve<T extends BdPostalCodeResolveStrategy>(
    postalCodes: BdPostalCode[],
    resolveStrategy: T,
  ): BdPostalCodeResolveType<T> {
    switch (resolveStrategy) {
      case 'DETAIL': {
        return BdPostalCode.fromObject({
          ...postalCodes[0],

          // sort all postal codes that: name === otlName is first
          name: postalCodes
            .sort((item) => (item.name === item.otlName ? -1 : 1))
            .map((it) => (it.otlName.length > 0 ? it.otlName : it.name))
            .join(', '),
        }) as BdPostalCodeResolveType<T>;
      }
      case 'SUMMARY': {
        const first = postalCodes[0];
        const messageMapping: { [k: string]: string } = {
          '=0': '',
          '=1': '(oder ein weiterer Ortsteil)',
          other: '(oder einer von weiteren # Ortsteilen)',
        };
        return BdPostalCode.fromObject({
          ...first,
          name: `${first.name} ${this._pluralPipe.transform(postalCodes.length - 1, messageMapping)}`,
        }) as BdPostalCodeResolveType<T>;
      }
      case 'RAW': {
        return postalCodes as BdPostalCodeResolveType<T>;
      }
      default: {
        return postalCodes[
          postalCodes.length - 1
        ] as BdPostalCodeResolveType<T>;
      }
    }
  }
}

interface PostalCodeResponse {
  postalCode: string;
  name: string;
  otlName: string;
  type: string;
  administrativeDistrictId: number;
}
